import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';

const AboutPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = React.useState(null);

  // Scroll to top on mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "How does the programme work?",
      answer: (
        <div className="space-y-4">
          <p>
            Kodo is a 6-week structured programme designed to help you gain clarity on your career direction. Each week focuses on a different theme:
          </p>
          <ul className="list-disc list-inside space-y-1 text-slate-600">
            <li><strong>Week 1:</strong> Self-Discovery & Reflection</li>
            <li><strong>Week 2:</strong> Skills & Strengths Assessment</li>
            <li><strong>Week 3:</strong> Vision & Possibilities</li>
            <li><strong>Week 4:</strong> Obstacles & Reality Check</li>
            <li><strong>Week 5:</strong> Strategy & Planning</li>
            <li><strong>Week 6:</strong> Action & Accountability</li>
          </ul>
          <p>
            Each week, you'll complete activities and receive personalised insights based on your responses. At the end of 6 weeks, you'll receive your full programme record and a certificate of completion.
          </p>
          <p>
            The programme uses evidence-based self-reflection exercises — helping you identify thought patterns, challenge assumptions, and take meaningful action.
          </p>
          <p>
            It's created by humans who care deeply about career development, and uses AI to provide personalised insights tailored specifically to you.
          </p>
        </div>
      )
    },
    {
      question: "Why is this better than 1:1 career advice?",
      answer: (
        <div className="space-y-4">
          <p>Great question! Here's why Kodo might be a better fit for you:</p>
          <ul className="space-y-3">
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">💰</span>
              <span><strong>Far more affordable.</strong> Traditional 1:1 career coaching typically costs £60+ per session. A full programme could set you back hundreds of pounds.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">⏰</span>
              <span><strong>On your schedule.</strong> No booking appointments, no waiting rooms. Work through the programme whenever suits you — at 2am in your pyjamas if that's your thing.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">🤫</span>
              <span><strong>Easier to be honest.</strong> Sometimes it's easier to open up when you're not being judged by another human. You can be completely candid about your fears, doubts, and dreams.</span>
            </li>
          </ul>
        </div>
      )
    },
    {
      question: "How much does it cost?",
      answer: (
        <div className="space-y-4">
          <p className="text-xl font-bold text-primary">Currently, it's completely free! 🎉</p>
          <p>
            We're in the early stages of building Kodo, and we want to get feedback from real users to make the product better.
          </p>
          <p>
            In the future, we may introduce pricing — but everyone who joins now gets full access at no cost.
          </p>
          <p className="text-slate-500 italic">
            (So if you've been thinking about it... now's a pretty good time to jump in!)
          </p>
        </div>
      )
    },
    {
      question: "How do you use my data?",
      answer: (
        <div className="space-y-4">
          <p>We take your privacy seriously. Here's what you need to know:</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">🤖</span>
              <span>Your responses are sent to an AI (Large Language Model) to generate personalised insights.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">🔒</span>
              <span>Your data is stored securely for 1 year, then automatically deleted.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">🙈</span>
              <span>No one else can access your data — it's completely private to you.</span>
            </li>
          </ul>
          <p>
            For more details, see our{' '}
            <button
              onClick={() => window.location.href = '/terms'}
              className="text-primary font-semibold hover:underline"
            >
              Terms & Conditions
            </button>
          </p>
        </div>
      )
    },
    {
      question: "What if I have another question?",
      answer: (
        <div className="space-y-4">
          <p>We'd love to hear from you!</p>
          <p>
            Drop us an email at{' '}
            <a 
              href="mailto:hello@getkodo.co.uk" 
              className="text-primary font-semibold hover:underline"
            >
              hello@getkodo.co.uk
            </a>
          </p>
          <p className="text-slate-500">
            We read every message and try to respond within 24 hours.
          </p>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/')}
              data-testid="back-button"
              variant="ghost"
              className="rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-bold text-slate-900">
              About Us
            </h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-3xl mx-auto">

          {/* Hero Section */}
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Hey there! 👋
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              We're a small team from the UK & Taiwan 🇬🇧 🇹🇼, passionate about helping people find their way in their careers.
            </p>
          </div>

          {/* Fun Stats */}
          <div className="grid grid-cols-3 gap-4 mb-12">
            <div className="bg-white rounded-2xl p-6 text-center border border-slate-100 shadow-sm">
              <div className="text-4xl mb-2">🧠</div>
              <div className="text-2xl font-bold text-primary">20</div>
              <div className="text-sm text-slate-500">Years in digital</div>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center border border-slate-100 shadow-sm">
              <div className="text-4xl mb-2">☕</div>
              <div className="text-2xl font-bold text-primary">∞</div>
              <div className="text-sm text-slate-500">Coffees consumed</div>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center border border-slate-100 shadow-sm">
              <div className="text-4xl mb-2">💡</div>
              <div className="text-2xl font-bold text-primary">1</div>
              <div className="text-sm text-slate-500">Big mission</div>
            </div>
          </div>

          {/* About Section */}
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 mb-12">
            <p className="text-lg text-slate-600 leading-relaxed text-center">
              Our backgrounds span psychology, career development, and AI. We built Kodo because <strong>we believe everyone deserves access to quality career guidance</strong> — not just those who can afford expensive coaches.
            </p>
          </div>

          {/* FAQs Section */}
          <div className="mb-12">
            <h3 className="font-heading text-2xl font-bold text-slate-900 mb-6 text-center">
              Frequently Asked Questions
            </h3>
            
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl border border-slate-100 overflow-hidden"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                    data-testid={`faq-${index}-button`}
                  >
                    <span className="font-semibold text-slate-900">{faq.question}</span>
                    <ChevronDown 
                      className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${openFaq === index ? 'rotate-180' : ''}`}
                    />
                  </button>
                  <div 
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      openFaq === index ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-6 pb-6 text-slate-600">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 text-white text-center">
            <Mail className="w-10 h-10 mx-auto mb-4 opacity-80" />
            <h3 className="font-heading text-2xl font-bold mb-2">
              Got questions?
            </h3>
            <p className="text-blue-100 mb-4">
              We're always happy to chat.
            </p>
            <a 
              href="mailto:hello@getkodo.co.uk"
              className="inline-block px-6 py-3 bg-white text-primary font-semibold rounded-full hover:bg-blue-50 transition-colors"
            >
              hello@getkodo.co.uk
            </a>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-8">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-slate-500 hover:text-slate-700"
            >
              ← Back to Home
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
